import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';

import { isCancelError, refreshCancelToken } from '@/apis/axios.utils';
import * as socialListeningApi from '@/apis/social-listening';
import { useSocialListening } from '@/app/socialListening/composables/useSocialListening';

export const useSocialListeningTopPostsStore = defineStore('socialListeningTopPosts', () => {
  const { organizationId, isTrendsPage, isComparisonPage } = useSocialListening();

  const requestContext = reactive({});

  const trendsTopPostsPreview = ref([]);
  const trendsTopPosts = ref([]);

  const topicsTopPostsPreview = ref([]);
  const topicsTopPosts = ref([]);

  const topicCreatorTopPostsPreview = ref([]);
  const topicCreatorTopPosts = ref([]);

  /*
   * For any top posts usage that isn't for the SocialListeningTopPosts drawer in Trends / Topics
   */
  const trendsDrawerTopPostsPreview = ref([]);
  const trendsDrawerTopPosts = ref([]);

  const topicsDrawerTopPostsPreview = ref([]);
  const topicsDrawerTopPosts = ref([]);

  const comparisonTopPosts = ref([]);
  const comparisonDrawerTopPosts = ref([]);

  const topicsCommentsPreview = ref([]);

  const pending = ref({
    trendsTopPostsPreview: false,
    trendsTopPosts: false,
    topicsTopPostsPreview: false,
    topicsTopPosts: false,
    topicCreatorTopPostsPreview: false,
    topicCreatorTopPosts: false,
    trendsDrawerTopPostsPreview: false,
    trendsDrawerTopPosts: false,
    topicsDrawerTopPostsPreview: false,
    topicsDrawerTopPosts: false,
    comparisonTopPosts: false,
    comparisonDrawerTopPosts: false,
    topicsCommentsPreview: false,
  });

  // Computed for targeting the page top posts preview (topics / trends)
  const targetPageTopPostsPreview = computed(() =>
    isTrendsPage.value ? trendsTopPostsPreview.value : topicsTopPostsPreview.value,
  );

  // Computed for targeting the page top posts (topics / trends)
  const targetPageTopPosts = computed(() => {
    if (isTrendsPage.value) return trendsTopPosts.value;
    if (isComparisonPage.value) return comparisonTopPosts.value;
    return topicsTopPosts.value;
  });

  // Computed for targeting the drawer top posts (topics / trends)
  const targetDrawerTopPosts = computed(() => {
    if (isTrendsPage.value) return trendsDrawerTopPosts.value;
    if (isComparisonPage.value) return comparisonDrawerTopPosts.value;
    return topicsDrawerTopPosts.value;
  });

  // Computed for targeting the page pending top posts preview (topics / trends)
  const targetPagePendingTopPostsPreview = computed(() =>
    isTrendsPage.value ? pending.value.trendsTopPostsPreview : pending.value.topicsTopPostsPreview,
  );

  // Computed for targeting the page pending top posts  (topics / trends)
  const targetPagePendingTopPosts = computed(() => {
    if (isTrendsPage.value) return pending.value.trendsTopPosts;
    if (isComparisonPage.value) return pending.value.comparisonTopPosts;
    return pending.value.topicsTopPosts;
  });

  const targetDrawerPendingTopPosts = computed(() => {
    if (isTrendsPage.value) return pending.value.trendsDrawerTopPosts;
    if (isComparisonPage.value) return pending.value.comparisonDrawerTopPosts;
    return pending.value.topicsDrawerTopPosts;
  });

  function getTopPostRef(key) {
    return {
      trendsTopPostsPreview,
      trendsTopPosts,
      topicsTopPostsPreview,
      topicsTopPosts,
      topicCreatorTopPostsPreview,
      topicCreatorTopPosts,
      trendsDrawerTopPostsPreview,
      trendsDrawerTopPosts,
      topicsDrawerTopPostsPreview,
      topicsDrawerTopPosts,
      comparisonTopPosts,
      comparisonDrawerTopPosts,
      topicsCommentsPreview,
    }[key];
  }

  function clearTopPostsPreview() {
    trendsTopPostsPreview.value = [];
    topicsTopPostsPreview.value = [];
  }

  function clearTopPostsData() {
    trendsTopPosts.value = [];
    topicsTopPosts.value = [];
    topicCreatorTopPosts.value = [];
    trendsDrawerTopPosts.value = [];
    topicsDrawerTopPosts.value = [];
    comparisonTopPosts.value = [];
    comparisonDrawerTopPosts.value = [];
  }

  function clearTopicsPageTopPostsData() {
    topicsTopPosts.value = [];
    topicsTopPostsPreview.value = [];
    topicsDrawerTopPosts.value = [];
    topicsDrawerTopPostsPreview.value = [];
  }

  function clearCommentsPreview() {
    topicsCommentsPreview.value = [];
  }

  function setTopPostsPreviewPending() {
    pending.value.trendsTopPostsPreview = true;
    pending.value.topicsTopPostsPreview = true;
  }

  async function fetchTopPosts(stateKey, payload) {
    pending.value[stateKey] = true;
    const stateRef = getTopPostRef(stateKey);

    const cancelToken = refreshCancelToken(requestContext, stateKey);

    try {
      const res = await socialListeningApi.getSearchResults(
        { organizationId: organizationId.value, payload },
        { cancelToken },
      );
      if (payload?.paging?.offset) {
        stateRef.value = stateRef.value.concat(res.data.data);
      } else {
        stateRef.value = res.data.data;
      }
    } catch (err) {
      if (!isCancelError(err)) return;
      stateRef.value = [];
    }
    pending.value[stateKey] = false;
  }

  return {
    pending,
    trendsTopPostsPreview,
    trendsTopPosts,
    topicsTopPostsPreview,
    topicsTopPosts,
    topicCreatorTopPostsPreview,
    topicCreatorTopPosts,
    trendsDrawerTopPostsPreview,
    trendsDrawerTopPosts,
    topicsDrawerTopPostsPreview,
    topicsDrawerTopPosts,
    topicsCommentsPreview,
    targetPageTopPostsPreview,
    targetPageTopPosts,
    targetPagePendingTopPostsPreview,
    targetPagePendingTopPosts,
    targetDrawerTopPosts,
    targetDrawerPendingTopPosts,
    clearTopPostsPreview,
    clearTopPostsData,
    clearTopicsPageTopPostsData,
    clearCommentsPreview,
    setTopPostsPreviewPending,
    fetchTopPosts,
  };
});
